import { prepareInspectionImage } from '../lib/image';
import { supabase } from '../lib/supabase';
import type { InspectionCheck, InspectionResponseRecord } from '../types/inspection';
import type { EvidenceRecord, IssuePriority, IssueRecord, IssueWithEvidence } from '../types/issues';

function priorityFromCheck(check: InspectionCheck): IssuePriority {
  if (check.severity === 'critical') return 'urgent';
  if (check.severity === 'major') return 'important';
  return 'minor';
}

export async function getOrCreateIssueForResponse(
  ownerId: string,
  assessmentId: string,
  response: InspectionResponseRecord,
  check: InspectionCheck,
): Promise<IssueRecord> {
  const { data: existing, error: existingError } = await supabase
    .from('issues')
    .select(
      'id, assessment_id, response_id, title, description, location, priority, status, created_at, updated_at',
    )
    .eq('owner_id', ownerId)
    .eq('assessment_id', assessmentId)
    .eq('response_id', response.id)
    .limit(1)
    .maybeSingle();

  if (existingError) throw existingError;
  if (existing) return existing as IssueRecord;

  const { data, error } = await supabase
    .from('issues')
    .insert({
      owner_id: ownerId,
      assessment_id: assessmentId,
      response_id: response.id,
      title: check.title,
      description:
        response.answer === 'not_sure'
          ? 'Buyer marked this check as not sure and wants it retained for review.'
          : check.warningSign,
      location: check.area,
      priority: priorityFromCheck(check),
      status: 'open',
    })
    .select(
      'id, assessment_id, response_id, title, description, location, priority, status, created_at, updated_at',
    )
    .single();

  if (error) throw error;
  return data as IssueRecord;
}

export async function updateIssueDetails(
  ownerId: string,
  issueId: string,
  description: string,
  priority: IssuePriority,
): Promise<IssueRecord> {
  const { data, error } = await supabase
    .from('issues')
    .update({ description: description.trim() || null, priority })
    .eq('id', issueId)
    .eq('owner_id', ownerId)
    .select(
      'id, assessment_id, response_id, title, description, location, priority, status, created_at, updated_at',
    )
    .single();

  if (error) throw error;
  return data as IssueRecord;
}

export async function uploadIssueEvidence(
  ownerId: string,
  assessmentId: string,
  issueId: string,
  file: File,
  note: string | null,
): Promise<EvidenceRecord> {
  const prepared = await prepareInspectionImage(file);
  const extension = prepared.name.split('.').pop() || 'jpg';
  const path = `${ownerId}/${assessmentId}/${issueId}/${crypto.randomUUID()}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from('inspection-evidence')
    .upload(path, prepared, {
      cacheControl: '3600',
      contentType: prepared.type,
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data, error } = await supabase
    .from('evidence')
    .insert({
      owner_id: ownerId,
      issue_id: issueId,
      storage_path: path,
      file_name: prepared.name,
      mime_type: prepared.type,
      size_bytes: prepared.size,
      evidence_type: 'issue',
      note: note?.trim() || null,
      captured_at: new Date().toISOString(),
    })
    .select(
      'id, issue_id, storage_path, file_name, mime_type, size_bytes, evidence_type, note, captured_at, created_at',
    )
    .single();

  if (error) {
    await supabase.storage.from('inspection-evidence').remove([path]);
    throw error;
  }

  return data as EvidenceRecord;
}

export async function loadIssueRegister(
  ownerId: string,
  assessmentId: string,
): Promise<IssueWithEvidence[]> {
  const { data: issues, error: issueError } = await supabase
    .from('issues')
    .select(
      'id, assessment_id, response_id, title, description, location, priority, status, created_at, updated_at',
    )
    .eq('owner_id', ownerId)
    .eq('assessment_id', assessmentId)
    .order('created_at', { ascending: false });

  if (issueError) throw issueError;

  const issueRows = (issues ?? []) as IssueRecord[];
  if (issueRows.length === 0) return [];

  const issueIds = issueRows.map((item) => item.id);
  const { data: evidence, error: evidenceError } = await supabase
    .from('evidence')
    .select(
      'id, issue_id, storage_path, file_name, mime_type, size_bytes, evidence_type, note, captured_at, created_at',
    )
    .eq('owner_id', ownerId)
    .in('issue_id', issueIds)
    .order('created_at', { ascending: true });

  if (evidenceError) throw evidenceError;

  const evidenceRows = (evidence ?? []) as EvidenceRecord[];
  return issueRows.map((issue) => ({
    ...issue,
    evidence: evidenceRows.filter((item) => item.issue_id === issue.id),
  }));
}

export async function createEvidencePreviewUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('inspection-evidence')
    .createSignedUrl(storagePath, 300);

  if (error) throw error;
  return data.signedUrl;
}
